import React, { useEffect, useState } from "react";

import { Film, Tags, UserCheck } from "lucide-react";

import AssignmentsPanel from "./AssignmentsPanel";
import LibraryPanel from "./LibraryPanel";
import TopicsPanel from "./TopicsPanel";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchAdminVideos,
  fetchLibraryVideos,
  fetchVideoTopics,
} from "../../Store/Reducers/VideoSlice";
import "./admin-video-shared.scss";
import "./style.scss";

type WorkspaceTab = "library" | "topics" | "assignments";

const TABS: Array<{
  id: WorkspaceTab;
  label: string;
  icon: React.ReactNode;
  countKey?: "libraryVideos" | "topics";
}> = [
  {
    id: "library",
    label: "Video Library",
    icon: <Film className="h-4 w-4" />,
    countKey: "libraryVideos",
  },
  {
    id: "topics",
    label: "Topics",
    icon: <Tags className="h-4 w-4" />,
    countKey: "topics",
  },
  {
    id: "assignments",
    label: "Role Assignments",
    icon: <UserCheck className="h-4 w-4" />,
  },
];

/**
 * Videos are added once in the library, grouped into topics, and assigned to
 * roles separately.
 */
const AdminVideoWorkspace: React.FC = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<WorkspaceTab>("library");
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const { libraryVideos, topics } = useAppSelector(state => state.video);

  useEffect(() => {
    dispatch(fetchLibraryVideos());
    dispatch(fetchVideoTopics());
    dispatch(fetchAdminVideos({}));
  }, [dispatch]);

  const counts = {
    libraryVideos: libraryVideos.length,
    topics: topics.length,
  };

  return (
    <div className="admin-video-workspace">
      <div className="admin-video-workspace__tab-row">
        <div
          className="inline-flex h-10 shrink-0 items-center gap-0.5 rounded-full border border-gray-200 bg-white px-1"
          role="tablist"
        >
          {TABS.map(tab => {
            const isActive = activeTab === tab.id;
            const count =
              tab.countKey !== undefined ? counts[tab.countKey] : undefined;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1 text-sm font-medium transition-all ${
                  isActive
                    ? "border border-purple-500 bg-white font-semibold text-purple-600"
                    : "border border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.icon}
                {tab.label}
                {count !== undefined && (
                  <span
                    className={`rounded-full px-1.5 text-xs font-semibold ${
                      isActive
                        ? "bg-purple-100 text-purple-600"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <div
          ref={setActionsSlot}
          className="admin-video-workspace__tab-actions"
        />
      </div>

      {activeTab === "library" && <LibraryPanel actionsSlot={actionsSlot} />}
      {activeTab === "topics" && <TopicsPanel actionsSlot={actionsSlot} />}
      {activeTab === "assignments" && (
        <AssignmentsPanel actionsSlot={actionsSlot} />
      )}
    </div>
  );
};

export default AdminVideoWorkspace;
